// Copied from rakerone-web/src/components/grounding/audio-section.tsx; i18n
// stripped, transcript passed inline (the run carries it) with speaker colours.
import { memo, useEffect, useRef } from 'react'

import { cn } from '../../lib/utils'

export function formatTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export type TranscriptSegment = { start?: number; end?: number; text?: string; speaker?: string }

const DIARIZED_SPEAKER = /^speaker[_\s-]?0*(\d+)$/i
export function speakerNumber(speaker: string): number | null {
  const match = DIARIZED_SPEAKER.exec(speaker.trim())
  return match ? Number(match[1]) + 1 : null
}
const SPEAKER_COLORS = [
  'bg-accent-soft text-primary',
  'bg-clay-soft text-clay',
  'bg-olive-soft text-olive',
  'bg-paper-3 text-ink-2',
]
export const speakerColor = (speaker: string) =>
  SPEAKER_COLORS[((speakerNumber(speaker) ?? 1) - 1) % SPEAKER_COLORS.length]

/** The LAST segment whose start has passed — stable through inter-segment silence. */
export function activeSegmentIndex(segments: TranscriptSegment[], currentTime: number): number {
  let active = -1
  for (let i = 0; i < segments.length; i++) {
    const start = segments[i]?.start
    if (typeof start === 'number' && start <= currentTime) active = i
  }
  return active
}

export function seekAndPlay(audio: HTMLMediaElement, seconds: number) {
  const play = () => void audio.play().catch(() => undefined)
  if (Math.abs(audio.currentTime - seconds) < 0.01) return play()
  audio.addEventListener('seeked', play, { once: true })
  audio.currentTime = seconds
}

export function AudioSection({
  url,
  segments,
  seekTo,
  seekRequest,
}: {
  url: string
  segments: TranscriptSegment[]
  seekTo?: number
  /** Changes on every request to seek, so an unchanged `seekTo` still jumps. */
  seekRequest?: number
}) {
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || seekTo === undefined) return
    const apply = () => seekAndPlay(audio, seekTo)
    if (audio.readyState >= 1) return apply()
    audio.addEventListener('loadedmetadata', apply, { once: true })
    return () => audio.removeEventListener('loadedmetadata', apply)
  }, [seekTo, seekRequest])

  const onSeek = (seconds: number) => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.readyState >= 1) seekAndPlay(audio, seconds)
    else audio.addEventListener('loadedmetadata', () => seekAndPlay(audio, seconds), { once: true })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {/* The synced transcript below is the text alternative. */}
      <audio ref={audioRef} controls preload="metadata" src={url} className="w-full" />
      <TranscriptView segments={segments} audioRef={audioRef} onSeek={onSeek} />
    </div>
  )
}

function TranscriptView({
  segments,
  audioRef,
  onSeek,
}: {
  segments: TranscriptSegment[]
  audioRef: React.RefObject<HTMLAudioElement | null>
  onSeek: (seconds: number) => void
}) {
  const activeRef = useRef(-1)
  const listRef = useRef<HTMLDivElement>(null)

  // Playback ticks ~4x/s: mark the active row imperatively, keep React out of it.
  useEffect(() => {
    const audio = audioRef.current
    const list = listRef.current
    if (!audio || !list) return
    activeRef.current = -1
    const sync = () => {
      const next = activeSegmentIndex(segments, audio.currentTime)
      if (next === activeRef.current) return
      const previous = list.children[activeRef.current]
      previous?.removeAttribute('data-active')
      previous?.removeAttribute('aria-current')
      activeRef.current = next
      const el = list.children[next]
      if (!(el instanceof HTMLElement)) return
      el.setAttribute('data-active', 'true')
      el.setAttribute('aria-current', 'true')
      const listRect = list.getBoundingClientRect()
      const elRect = el.getBoundingClientRect()
      if (elRect.top < listRect.top) list.scrollTop += elRect.top - listRect.top
      else if (elRect.bottom > listRect.bottom) list.scrollTop += elRect.bottom - listRect.bottom
    }
    sync()
    audio.addEventListener('timeupdate', sync)
    audio.addEventListener('seeked', sync)
    return () => {
      audio.removeEventListener('timeupdate', sync)
      audio.removeEventListener('seeked', sync)
    }
  }, [segments, audioRef])

  if (segments.length === 0)
    return <p className="text-sm text-muted-foreground">No transcript segments.</p>
  return (
    <div
      ref={listRef}
      className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2"
    >
      {segments.map((seg, i) => (
        <TranscriptRow key={i} segment={seg} onSeek={onSeek} />
      ))}
    </div>
  )
}

const TranscriptRow = memo(function TranscriptRow({
  segment,
  onSeek,
}: {
  segment: TranscriptSegment
  onSeek: (s: number) => void
}) {
  return (
    <button
      type="button"
      onClick={() => (typeof segment.start === 'number' ? onSeek(segment.start) : undefined)}
      className="flex flex-col gap-0.5 rounded-sm p-1.5 text-left transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none data-[active]:bg-accent"
    >
      <div className="flex items-center gap-2">
        {typeof segment.start === 'number' ? (
          <span className="font-mono text-xs text-muted-foreground">
            {formatTimecode(segment.start)}
          </span>
        ) : null}
        {segment.speaker ? (
          <span
            className={cn(
              'rounded-full px-1.5 py-px text-[10px] font-medium',
              speakerColor(segment.speaker),
            )}
          >
            {speakerNumber(segment.speaker) != null
              ? `Speaker ${speakerNumber(segment.speaker)}`
              : segment.speaker}
          </span>
        ) : null}
      </div>
      <p className="text-sm">{segment.text ?? ''}</p>
    </button>
  )
})
